import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AppleIcon from '@mui/icons-material/Apple';
import TopBar from '../components/TopBar';
import StoreMap from '../components/StoreMap';
import TroopZonePickerMap from '../components/TroopZonePickerMap';
import FindStoreByLocation from '../components/FindStoreByLocation';
import { getApiUrl } from '../apiUrl';
import { supabase, isSupabaseConfigured, getAccessToken } from '../supabaseClient';

const API_URL = getApiUrl();
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const Dashboard = () => {
  const [authChecked, setAuthChecked] = useState(false);
  const [userData, setUserData] = useState(null);
  const [username, setUsername] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [authError, setAuthError] = useState('');
  const [showFindStore, setShowFindStore] = useState(false);
  const [googleButtonReady, setGoogleButtonReady] = useState(false);

  const [onboardingStep, setOnboardingStep] = useState('username');
  const [zipValid, setZipValid] = useState(false);
  const [zipValidating, setZipValidating] = useState(false);
  const [userCenter, setUserCenter] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(false);
  const [usernameValidating, setUsernameValidating] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  const mapRef = useRef(null);
  const loadInFlightRef = useRef(false);
  const googleButtonRef = useRef(null);
  const usernameCheckSeqRef = useRef(0);
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    try {
      if (!supabase) throw new Error('Supabase env vars are missing.');
      const redirectTo = `${window.location.origin}/dashboard`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo }
      });
      if (error) throw error;
    } catch (error) {
      setAuthError(error.message || 'Google sign-in failed');
    }
  };

  const handleGoogleCredential = async (response) => {
    try {
      if (!supabase) throw new Error('Supabase env vars are missing.');
      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: response.credential
      });
      if (error) throw error;
    } catch (error) {
      setAuthError(error.message || 'Google sign-in failed');
    }
  };

  const loadAppProfile = async () => {
    if (loadInFlightRef.current) return;
    loadInFlightRef.current = true;
    const token = await getAccessToken();
    if (!token) {
      setUserData(null);
      setAuthChecked(true);
      loadInFlightRef.current = false;
      return;
    }

    try {
      await axios.post(`${API_URL}/api/profile/bootstrap`, {}, { headers: { Authorization: `Bearer ${token}` } });

      const response = await axios.get(`${API_URL}/check-session`, { headers: { Authorization: `Bearer ${token}` } });

      if (!response.data?.isLoggedIn) {
        setUserData(null);
      } else {
        setUserData(response.data);
        const center = response.data.mapCenter?.coordinates || null;
        setMapCenter(center);
        if (center) setUserLocation(center);
        setAuthError('');

        if (!response.data.username) {
          setOnboardingStep('username');
        } else if (!response.data.troopZoneId) {
          setOnboardingStep('zip');
          if (response.data.zipCode) setZipCode(response.data.zipCode);
        }
      }
    } catch (error) {
      console.error('Auth/profile load failed:', error.response?.data || error.message);
      setAuthError(error.response?.data?.error || error.message || 'Auth failed');
      setUserData(null);
    } finally {
      setAuthChecked(true);
      loadInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!supabase) {
      setAuthChecked(true);
      return undefined;
    }

    let cancelled = false;

    const init = async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      if (data?.session?.access_token) {
        await loadAppProfile();
        return;
      }

      setAuthChecked(true);
    };

    init();

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (cancelled) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (session?.access_token) {
          setAuthChecked(false);
          await loadAppProfile();
        }
      }
      if (event === 'SIGNED_OUT') {
        setUserData(null);
        setAuthChecked(true);
      }
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // Native Google Identity Services button (personalized when Google recognizes the account)
  useEffect(() => {
    if (!authChecked || userData || !GOOGLE_CLIENT_ID || !supabase) {
      setGoogleButtonReady(false);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let resizeObserver;
    let renderedWidth = 0;

    const renderGoogleButton = () => {
      if (cancelled) return;
      const googleId = window.google?.accounts?.id;
      const container = googleButtonRef.current;
      const containerWidth = Math.floor(container?.getBoundingClientRect().width || 0);
      if (!googleId || !container || containerWidth === 0) {
        if (attempts < 50) {
          attempts += 1;
          window.setTimeout(renderGoogleButton, 100);
        }
        return;
      }

      googleId.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        use_fedcm_for_prompt: true,
        auto_select: false
      });

      const width = Math.min(containerWidth, 400);
      if (width === renderedWidth) return;
      renderedWidth = width;
      container.innerHTML = '';
      googleId.renderButton(container, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        shape: 'rectangular',
        text: 'continue_with',
        logo_alignment: 'left',
        width: String(width)
      });
      setGoogleButtonReady(true);
    };

    window.requestAnimationFrame(renderGoogleButton);
    if (window.ResizeObserver && googleButtonRef.current) {
      resizeObserver = new ResizeObserver(renderGoogleButton);
      resizeObserver.observe(googleButtonRef.current);
    }

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
    };
  }, [authChecked, userData]);

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut();
    setUserData(null);
    navigate('/dashboard');
  };

  const saveUsername = async () => {
    const trimmed = username.trim();
    if (trimmed.length < 3) {
      setUsernameError('Username must be at least 3 characters.');
      setUsernameAvailable(false);
      return;
    }
    if (!/^[a-zA-Z0-9_.-]+$/.test(trimmed)) {
      setUsernameError('Use letters, numbers, underscores, periods, or hyphens.');
      setUsernameAvailable(false);
      return;
    }
    if (!usernameAvailable) {
      setUsernameError(usernameError || 'That username is taken — try another.');
      return;
    }

    setProfileLoading(true);
    setUsernameError('');
    try {
      await axios.post(`${API_URL}/update-profile`, { username: trimmed }, { timeout: 15000 });
      await loadAppProfile();
      setOnboardingStep('zip');
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Could not save username.';
      if (message.toLowerCase().includes('taken')) {
        setUsernameError('That username is taken — try another.');
        setUsernameAvailable(false);
      } else {
        setUsernameError(message);
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const checkUsernameAvailability = async (value) => {
    const trimmed = value.trim();
    if (trimmed.length < 3) {
      setUsernameAvailable(false);
      setUsernameError(trimmed.length === 0 ? '' : 'Username must be at least 3 characters.');
      setUsernameValidating(false);
      return;
    }
    if (!/^[a-zA-Z0-9_.-]+$/.test(trimmed)) {
      setUsernameAvailable(false);
      setUsernameError('Use letters, numbers, underscores, periods, or hyphens.');
      setUsernameValidating(false);
      return;
    }
    if (trimmed.length > 24) {
      setUsernameAvailable(false);
      setUsernameError('Username must be 24 characters or fewer.');
      setUsernameValidating(false);
      return;
    }

    const seq = ++usernameCheckSeqRef.current;
    setUsernameValidating(true);
    try {
      const response = await axios.get(`${API_URL}/api/username/available`, {
        params: { username: trimmed },
        timeout: 10000
      });
      if (seq !== usernameCheckSeqRef.current) return;
      if (response.data?.available) {
        setUsernameAvailable(true);
        setUsernameError('');
      } else {
        setUsernameAvailable(false);
        setUsernameError(response.data?.reason || 'That username is taken — try another.');
      }
    } catch (error) {
      if (seq !== usernameCheckSeqRef.current) return;
      setUsernameAvailable(false);
      setUsernameError(error.response?.data?.error || 'Could not check username. Try again.');
    } finally {
      if (seq === usernameCheckSeqRef.current) setUsernameValidating(false);
    }
  };

  useEffect(() => {
    if (onboardingStep !== 'username') return undefined;
    const trimmed = username.trim();
    if (!trimmed) {
      setUsernameAvailable(false);
      setUsernameError('');
      setUsernameValidating(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      checkUsernameAvailability(trimmed);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [username, onboardingStep]);

  const validateZip = async (zip) => {
    if (!/^\d{5}$/.test(zip)) {
      setZipValid(false);
      return;
    }
    setZipValidating(true);
    try {
      const geo = await axios.get(`${API_URL}/api/geocode/zip`, { params: { zip } });
      if (geo.data?.success) {
        setUserCenter(geo.data.coordinates);
        setZipValid(true);
      } else {
        setZipValid(false);
      }
    } catch (_err) {
      setZipValid(false);
    } finally {
      setZipValidating(false);
    }
  };

  useEffect(() => {
    if (onboardingStep === 'zip' && zipCode.length === 5) {
      validateZip(zipCode);
    } else if (onboardingStep === 'zip') {
      setZipValid(false);
    }
  }, [zipCode, onboardingStep]);

  const goToTroopPicker = async () => {
    if (!zipValid || !userCenter) return;
    setProfileLoading(true);
    try {
      await axios.post(`${API_URL}/update-profile`, { zipCode });
      const nearby = await axios.get(`${API_URL}/api/troop-zones/nearby`, {
        params: { lat: userCenter[1], lng: userCenter[0], limit: 20 }
      });
      setZones(nearby.data || []);
      if (nearby.data?.[0]) setSelectedZoneId(nearby.data[0].id);
      setOnboardingStep('troop');
    } catch (error) {
      alert('Could not load troops: ' + (error.response?.data?.error || error.message));
    } finally {
      setProfileLoading(false);
    }
  };

  const joinTroop = async () => {
    if (!selectedZoneId) return;
    setProfileLoading(true);
    try {
      await axios.post(`${API_URL}/update-profile`, {
        troopZoneId: selectedZoneId,
        zipCode
      });
      await loadAppProfile();
    } catch (error) {
      alert('Join troop failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setProfileLoading(false);
    }
  };

  if (!authChecked) return <div>Loading...</div>;

  if (!isSupabaseConfigured) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="error" gutterBottom>Supabase config missing</Typography>
            <Typography>
              Add `REACT_APP_SUPABASE_URL` and `REACT_APP_SUPABASE_PUBLISHABLE_KEY` to `client/.env`, then restart `npm start`.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!userData) {
    return (
      <Box sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/bg1.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(6px) brightness(0.62) saturate(1.12)',
          transform: 'scale(1.04)',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(145deg, rgba(51,27,101,0.2), rgba(184,194,178,0.14))',
          zIndex: 0
        }
      }}>
        <Container
          maxWidth="sm"
          sx={{
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3 },
            py: 3
          }}
        >
          <Card
            sx={{
              overflow: 'visible',
              backgroundImage: 'url(/accenting-bg.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: { xs: 3, sm: 4 },
              border: '1px solid rgba(255,255,255,0.58)',
              boxShadow: '0 22px 60px rgba(11,16,22,0.42)',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 8,
                border: '1px dashed rgba(51,27,101,0.3)',
                borderRadius: { xs: 2.25, sm: 3.25 },
                pointerEvents: 'none'
              }
            }}
          >
            <CardContent
              sx={{
                position: 'relative',
                zIndex: 1,
                textAlign: 'center',
                px: { xs: 3, sm: 5 },
                pt: { xs: 3, sm: 4 },
                pb: { xs: 3.5, sm: 4.5 },
                '&:last-child': { pb: { xs: 3.5, sm: 4.5 } }
              }}
            >
              <Box
                component="img"
                src="/logo1.png"
                alt="Clearance Scouts"
                sx={{
                  display: 'block',
                  width: 'clamp(180px, 58vw, 330px)',
                  maxHeight: '42vh',
                  objectFit: 'contain',
                  mx: 'auto',
                  mb: { xs: 1.5, sm: 2 },
                  filter: 'drop-shadow(0 10px 14px rgba(42,28,80,0.3))'
                }}
              />
              <Typography
                component="h1"
                sx={{
                  color: '#2A1C50',
                  fontSize: { xs: '0.75rem', sm: '0.78rem' },
                  lineHeight: 1.3,
                  fontWeight: 900,
                  letterSpacing: '0.055em',
                  textTransform: 'uppercase',
                  textShadow: '0.7px 0.7px 0 #C0BED1, 1.9px 1.9px 0 rgba(48, 17, 116, 0.35)',
                                    filter: 'drop-shadow(0 3px 2px rgba(39, 50, 53, 0.72))',
               
                }}
              >
                Join the troop! 
              </Typography>
              <Typography
                   component="h1"
                   sx={{
                     color: '#2A1C50',
                     fontSize: { xs: '0.6rem', sm: '0.62rem' },
                     lineHeight: 1.3,
                     fontWeight: 900,
                     letterSpacing: '0.055em',
                     textTransform: 'uppercase',
                     textShadow: '0.7px 0.7px 0 #C0BED1, 1.9px 1.9px 0 rgba(42, 12, 105, 0.35)',
                     filter: 'drop-shadow(0 3px 2px rgba(32, 43, 42, 0.72))',
                     mb:2
                   }}
              
              >or resume your quest...</Typography>
              {authError && (
                <Typography sx={{ mb: 2, color: '#2A1C50', fontWeight: 600 }}>
                  {authError}
                </Typography>
              )}
              <Box
                ref={googleButtonRef}
                sx={{
                  mb: googleButtonReady ? 1.25 : 0,
                  minHeight: googleButtonReady ? 46 : 0,
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  maxWidth: 400,
                  mx: 'auto',
                  overflow: 'hidden',
                  '& > div': { width: '100% !important' },
                  '& iframe': { display: 'block', margin: '0 auto' }
                }}
              />
              {!googleButtonReady && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGoogleLogin}
                  sx={{
                    mb: 1.25,
                    minHeight: 46,
                  maxWidth: 400,
                    background: 'linear-gradient(135deg, #331B65 0%, #59458C 100%)',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                  boxShadow: '0 6px 14px rgba(51,27,101,0.25)',
                  mx: 'auto'
                  }}
                >
                  Continue with Google
                </Button>
              )}
              <Button
                variant="outlined"
                fullWidth
                disabled
                startIcon={<AppleIcon sx={{ color: '#2A1C50' }} />}
                sx={{
                  minHeight: 46,
                  maxWidth: 400,
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                  color: '#2A1C50',
                  borderColor: 'rgba(42,28,80,0.55)',
                  backgroundColor: 'rgba(42,28,80,0.1)',
                  '&.Mui-disabled': {
                    color: '#2A1C50',
                    borderColor: 'rgba(42,28,80,0.5)',
                    backgroundColor: 'rgba(42,28,80,0.1)',
                    opacity: 1
                  },
                  mx: 'auto'
                }}
              >
                Continue with Apple (Coming soon)
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const needsOnboarding = !userData.username || !userData.troopZoneId;

  if (needsOnboarding) {
    if (onboardingStep === 'troop' && userCenter) {
      return (
        <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <TopBar userData={userData} handleLogout={handleLogout} />
          <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
            <TroopZonePickerMap
              userCenter={userCenter}
              zones={zones}
              selectedZoneId={selectedZoneId}
              onSelectZone={setSelectedZoneId}
              onConfirm={joinTroop}
              onBack={() => setOnboardingStep('zip')}
              confirmLabel="Join this Troop"
              busy={profileLoading}
              fillParent
            />
          </Box>
        </Box>
      );
    }

    return (
      <>
        <TopBar userData={userData} handleLogout={handleLogout} />
        <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
          <Card>
            <CardContent>
              {onboardingStep === 'username' && (
                <>
                  <Typography variant="h6" gutterBottom>Choose a username</Typography>
                  <TextField
                    fullWidth
                    label="Username"
                    value={username}
                    onChange={(e) => {
                      const next = e.target.value.replace(/[^a-zA-Z0-9_.-]/g, '').slice(0, 24);
                      setUsername(next);
                      setUsernameError('');
                      setUsernameAvailable(false);
                    }}
                    error={Boolean(usernameError)}
                    helperText={
                      usernameError
                        || (usernameAvailable ? 'Looks good — that username is available.' : ' ')
                    }
                    FormHelperTextProps={{
                      sx: {
                        color: usernameError ? 'error.main' : usernameAvailable ? 'success.main' : 'text.secondary',
                        minHeight: 20
                      }
                    }}
                    sx={{ mb: 2 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {usernameValidating ? (
                            <CircularProgress size={22} />
                          ) : usernameAvailable ? (
                            <CheckCircleIcon sx={{ color: 'success.main' }} />
                          ) : null}
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={saveUsername}
                    disabled={profileLoading || usernameValidating || !usernameAvailable || username.trim().length < 3}
                    sx={{ backgroundColor: '#331B65' }}
                  >
                    {profileLoading ? 'Saving...' : 'Continue'}
                  </Button>
                </>
              )}

              {onboardingStep === 'zip' && (
                <>
                  <Typography variant="h6" gutterBottom>Where are you shopping from?</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter your zip so we can show nearby troop zones.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    sx={{ mb: 2 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {zipValidating ? (
                            <CircularProgress size={24} />
                          ) : zipValid ? (
                            <IconButton edge="end" sx={{ color: 'green' }}>
                              <CheckCircleIcon />
                            </IconButton>
                          ) : (
                            <IconButton edge="end" disabled sx={{ color: 'grey' }}>
                              <ArrowForwardIcon />
                            </IconButton>
                          )}
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={goToTroopPicker}
                    disabled={!zipValid || profileLoading}
                    sx={{ backgroundColor: '#331B65' }}
                  >
                    {profileLoading ? 'Loading...' : 'Find Troops'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Container>
      </>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <TopBar
        userData={userData}
        handleLogout={handleLogout}
        onAddEventClick={() => setShowFindStore(true)}
      />
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <StoreMap
          ref={mapRef}
          userLocation={userLocation || mapCenter}
          mapCenter={mapCenter}
          mapZoom={userData.mapZoom || 11}
          notificationRadius={userData.notificationRadius || userData.troopZone?.radiusMiles || 10}
          onProfileRefresh={loadAppProfile}
        />
      </Box>
      <FindStoreByLocation
        open={showFindStore}
        onClose={() => setShowFindStore(false)}
        mapCenter={mapCenter}
        onStoreSelect={(store) => {
          mapRef.current?.openAddEventForStore?.(store);
        }}
        onLocationObtained={(location) => {
          setUserLocation(location);
        }}
      />
    </Box>
  );
};

export default Dashboard;
